import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { FileText, Download, Loader2, ChevronLeft, CheckCircle2, PenLine, Clock, AlertTriangle, Eraser } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TT = (lang, en, es) => (lang === 'es' ? es : en);

const STATUS_META = {
  'Signed': { cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', Icon: CheckCircle2 },
  'Pending': { cls: 'border-blue-200 bg-blue-50 text-blue-800', Icon: Clock },
  'Updated Version — Acknowledgment Required': { cls: 'border-amber-200 bg-amber-50 text-amber-800', Icon: AlertTriangle },
  'Not Yet Available — Template Pending Approval': { cls: 'border-slate-200 bg-slate-50 text-slate-600', Icon: Clock },
};
const STATUS_ES = {
  'Signed': 'Firmado',
  'Pending': 'Pendiente',
  'Updated Version — Acknowledgment Required': 'Versión Actualizada — Se Requiere Reconocimiento',
  'Not Yet Available — Template Pending Approval': 'Aún No Disponible — Plantilla Pendiente de Aprobación',
};

const SignModal = ({ vl, setVl, acks, onClose, onSigned, token }) => {
  const [name, setName] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const c = canvasRef.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = canvasRef.current.getContext('2d'); const p = pos(e); c.lineWidth = 2.2; c.lineCap = 'round'; c.strokeStyle = '#0E2A47'; c.lineTo(p.x, p.y); c.stroke(); dirty.current = true; };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirty.current = false; };

  const language = vl === 'es' ? 'Español' : 'English';
  const submit = async (asDraft) => {
    if (!asDraft) {
      if (!name.trim()) return toast.error(TT(vl, 'Enter your legal name', 'Ingrese su nombre legal'));
      if (!ack) return toast.error(TT(vl, 'Confirm the acknowledgment', 'Confirme el reconocimiento'));
      if (!dirty.current) return toast.error(TT(vl, 'Please sign', 'Por favor firme'));
    }
    const signature_image = dirty.current ? canvasRef.current.toDataURL('image/png') : null;
    setBusy(true);
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      if (asDraft) {
        await axios.put(`${API}/api/team/guidelines/draft`, { method: 'drawn', language, typed_name: name, acknowledged: ack, signature_image }, h);
        toast.success(TT(vl, 'Draft saved', 'Borrador guardado')); onClose();
      } else {
        await axios.post(`${API}/api/team/guidelines/sign`, { method: 'drawn', language, typed_name: name, acknowledged: ack, signature_image }, h);
        toast.success(TT(vl, 'Acknowledgment signed', 'Reconocimiento firmado')); onSigned();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="team-gl-sign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><PenLine size={16} className="text-[#1763B6]" />{TT(vl, 'Review & Sign Acknowledgment', 'Revisar y Firmar Reconocimiento')}</DialogTitle>
          <DialogDescription className="text-[12px]">{TT(vl, 'Review the full guidelines above, then acknowledge and sign below.', 'Revise las directrices completas arriba, luego reconozca y firme a continuación.')}</DialogDescription>
        </DialogHeader>
        <div className="inline-flex overflow-hidden rounded-full ring-1 ring-slate-300">
          {['en', 'es'].map((l) => (
            <button key={l} onClick={() => setVl(l)} data-testid={`team-gl-sign-lang-${l}`} className={`px-3 py-1 text-xs font-bold ${vl === l ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-600'}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <div className="rounded-lg bg-slate-50 p-3 text-[12px] leading-relaxed text-slate-700" data-testid="team-gl-ack-text">
          {(acks[vl] || []).map((a, i) => <p key={i} className="mb-1.5">{a}</p>)}
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-gl-sign-name" placeholder={TT(vl, 'Typed legal name', 'Nombre legal escrito')} className="w-full rounded border border-slate-300 px-3 py-2 text-sm" />
        <label className="flex items-start gap-2 text-[12px] text-slate-700"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="team-gl-sign-ack" className="mt-0.5" />{TT(vl, 'I have read and acknowledge the TFS Company Guidelines & Standards.', 'He leído y reconozco las Normas y Directrices de TFS.')}</label>
        <div>
          <div className="mb-1 flex items-center justify-between"><span className="text-[11px] font-semibold text-slate-500">{TT(vl, 'Signature', 'Firma')}</span><button onClick={clear} className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="team-gl-sign-clear"><Eraser size={12} />{TT(vl, 'Clear', 'Borrar')}</button></div>
          <canvas ref={canvasRef} width={440} height={140} data-testid="team-gl-sign-canvas"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full touch-none rounded-lg border border-slate-300 bg-white" />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={() => submit(true)} disabled={busy} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-600" data-testid="team-gl-save-draft">{TT(vl, 'Save Draft', 'Guardar Borrador')}</button>
          <button onClick={() => submit(false)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#0E2A47] px-4 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid="team-gl-save-signed">{busy ? '…' : <><PenLine size={13} />{TT(vl, 'Save Signed Document', 'Guardar Documento Firmado')}</>}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Guidelines = ({ lang, token }) => {
  const [g, setG] = useState(null);
  const [vl, setVl] = useState(lang);
  const [st, setSt] = useState(null);
  const [signing, setSigning] = useState(false);

  const loadStatus = () => axios.get(`${API}/api/team/guidelines/status`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setSt(r.data)).catch(() => setSt(false));
  useEffect(() => {
    axios.get(`${API}/api/team/guidelines`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setG(r.data.guidelines)).catch(() => setG(false));
    loadStatus();
  }, [token]);
  if (g === null) return <div className="p-6 text-sm text-slate-500" data-testid="team-gl-loading"><Loader2 className="inline animate-spin" size={16} /></div>;
  if (g === false) return <div className="p-6 text-sm text-red-600">Unable to load guidelines.</div>;
  const sm = st && st.status ? (STATUS_META[st.status] || STATUS_META['Pending']) : null;
  return (
    <div data-testid="team-guidelines">
      {st && st.status && sm && (
        <div className={`mb-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${sm.cls}`} data-testid="team-gl-status">
          <sm.Icon size={15} />
          <span>{TT(vl, st.status, STATUS_ES[st.status] || st.status)}{st.status === 'Signed' && st.latest?.signed_at ? ` · ${st.latest.signed_at.slice(0, 10)}` : ''}</span>
          {st.status === 'Signed' && st.latest?.id && (
            <a href={`${API}/api/team/guidelines/signed/${st.latest.id}/pdf`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 underline" data-testid="team-gl-view-signed"><FileText size={12} />{TT(vl, 'View signed PDF', 'Ver PDF firmado')}</a>
          )}
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#0E2A47]" data-testid="team-gl-title">{g.title[vl]}</h2>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-testid="team-gl-rev">{TT(vl, 'Revision', 'Revisión')} {g.revision}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-full ring-1 ring-slate-300">
            {['en', 'es'].map((l) => (
              <button key={l} onClick={() => setVl(l)} data-testid={`team-gl-lang-${l}`}
                className={`px-3 py-1 text-xs font-bold ${vl === l ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-600'}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={() => window.print()} data-testid="team-gl-download" className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"><Download size={13} />{TT(vl, 'Download', 'Descargar')}</button>
        </div>
      </div>
      <Accordion type="single" collapsible className="space-y-2" data-testid="team-gl-accordion">
        {g.sections.map((s) => (
          <AccordionItem key={s.key} value={`s-${s.n}`} className="rounded-xl border border-slate-200 bg-white px-3" data-testid={`team-gl-sec-${s.n}`}>
            <AccordionTrigger className="py-3 text-left text-sm font-bold text-[#0E2A47] hover:no-underline" data-testid={`team-gl-sec-trigger-${s.n}`}>
              <span className="flex-1 pr-2">{s.n}. {s.title[vl]}</span>
              {s.status !== 'approved' && <span className="mr-1 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">{TT(vl, 'Pending', 'Pendiente')}</span>}
            </AccordionTrigger>
            <AccordionContent>
              <p className="whitespace-pre-line pb-2 text-[12.5px] leading-relaxed text-slate-700" data-testid={`team-gl-sec-body-${s.n}`}>
                {s.body[vl] || TT(vl, 'Approved wording pending — to be provided by the Global Administrator.', 'Texto aprobado pendiente — será proporcionado por el Administrador Global.')}
              </p>
              {s.video?.url && (
                <video controls src={s.video.url} className="mt-1 w-full rounded-lg" data-testid={`team-gl-sec-video-${s.n}`} />
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="mt-4">
        {st && st.can_sign ? (
          <button onClick={() => setSigning(true)} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0E2A47] px-4 py-3 text-sm font-bold text-white sm:w-auto" data-testid="team-gl-review-sign">
            <PenLine size={16} />{TT(vl, 'Review & Sign Acknowledgment', 'Revisar y Firmar Reconocimiento')}
          </button>
        ) : (
          <p className="text-[11px] text-slate-400" data-testid="team-gl-signing-note">
            {st && st.status === 'Signed'
              ? TT(vl, 'You have acknowledged these guidelines. Your individual pay terms are documented separately in the Acknowledgment of Pay.', 'Usted ha reconocido estas directrices. Sus términos de pago individuales se documentan por separado en el Reconocimiento de Pago.')
              : TT(vl, 'Pending Template Approval — the Acknowledgment is enabled after the Global Administrator approves the final wording.', 'Pendiente de Aprobación de Plantilla — el Reconocimiento se habilita después de que el Administrador Global apruebe el texto final.')}
          </p>
        )}
      </div>
      {signing && <SignModal vl={vl} setVl={setVl} acks={g.acknowledgment || { en: [], es: [] }} token={token}
        onClose={() => setSigning(false)} onSigned={() => { setSigning(false); loadStatus(); }} />}
    </div>
  );
};


const TeamResources = ({ lang = 'en', token: tokenProp, onBack }) => {
  const ctx = useContext(AuthContext);
  const token = tokenProp || ctx?.token;
  return (
    <div className="mx-auto max-w-3xl px-5 py-6" data-testid="team-resources">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1C4FC9]" data-testid="team-resources-back"><ChevronLeft size={16} />{TT(lang, 'Back to TEAM', 'Volver a TEAM')}</button>
      <h1 className="mb-0.5 text-2xl font-extrabold text-[#0E2A47]">{TT(lang, 'Employee Resources', 'Recursos del Empleado')}</h1>
      <p className="mb-4 text-sm font-semibold text-slate-500" data-testid="team-resources-subtitle">{TT(lang, 'Company Guidelines & Standards', 'Normas y Directrices de la Empresa')}</p>
      <Guidelines lang={lang} token={token} />
    </div>
  );
};

export default TeamResources;
